import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateDevProject,
  getDevProjects,
  deleteDevProjectById,
} from '../../features/projects/devprojectSlice';
import {
  Form,
  Input,
  Alert,
  Modal,
  Checkbox,
  Select,
  Typography,
  Divider,
  Button,
  Card,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import {
  EditOutlined,
  CodeOutlined,
  LinkOutlined,
  GithubOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  GlobalOutlined,
  CalendarOutlined,
  StarOutlined,
  StarFilled,
} from '@ant-design/icons';
import MediaPreview from '../../utils/MediaPreview'; // Import the MediaPreview component
import { techStackOptions } from '../../assets/data/stack';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { confirm } = Modal;

const RenderDevProj = ({ onEditContent }) => {
  const dispatch = useDispatch();
  const [success, setSuccess] = useState(false);
  const [failed, setFailed] = useState(false);
  const { devprojects } = useSelector((state) => state.devprojects);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [form] = Form.useForm();


  const groupedStacks = techStackOptions.reduce((acc, stack) => {
    if (!acc[stack.category]) {
      acc[stack.category] = [];
    }
    acc[stack.category].push(stack);
    return acc;
  }, {});

  useEffect(() => {
    dispatch(getDevProjects());
  }, [dispatch]);

  // Helper function to check if content is truly empty
  const isContentEmpty = (content) => {
    if (!content) return true;

    // Remove HTML tags and check if any meaningful content remains
    const textContent = content
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .trim(); // Remove whitespace

    return textContent === '';
  };

  // Delete project with confirmation
  const handleDeleteId = (project) => {
    confirm({
      title: 'Delete Development Project',
      content: (
        <div>
          <p>
            Are you sure you want to delete <strong>"{project.name}"</strong>?
          </p>
          <p className='text-red-600'>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        dispatch(deleteDevProjectById(project._id))
          .then(() => {
            dispatch(getDevProjects());
          })
          .catch(() => {
            console.log('Problem deleting project');
          });
      },
    });
  };

  // Edit project - now uses modal for basic info only
  const handleEdit = (project) => {
    setIsModalVisible(true);
    setEditingProject(project);

    // Set form values with project data
    form.setFieldsValue({
      name: project.name,
      desc: project.desc,
      link: project.link,
      github: project.github,
      status: project.status,
      type: project.type,
      priority: project.priority,
      stacks: project.stacks || [],
    });
  };

  // Cancel the modal
  const handleCancel = () => {
    form.resetFields();
    setIsModalVisible(false);
    setEditingProject(null);
  };

  // Handle the submit event
  const handleOk = () => {
    form.submit();
  };

  const sortedProjects = [...devprojects].sort((a, b) => {
    // Sort by priority first (featured first), then by creation date
    if (a.priority === 'featured' && b.priority !== 'featured') return -1;
    if (a.priority !== 'featured' && b.priority === 'featured') return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const handleFormSubmit = async (values) => {
    try {
      console.log('Update form values:', values);

      const { name, desc, link, github, status, type, priority, stacks } = values;

      const formData = new FormData();
      formData.append('name', name);
      formData.append('desc', desc);
      formData.append('link', link || '');
      formData.append('github', github || '');
      formData.append('status', status.toString());
      formData.append('type', type);
      formData.append('priority', priority || '');
      formData.append(
        'stacks',
        Array.isArray(stacks) ? stacks.join(',') : stacks
      );

      await dispatch(
        updateDevProject({ id: editingProject._id, formData })
      ).unwrap();
      dispatch(getDevProjects());
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);

      form.resetFields();
      setIsModalVisible(false);
      setEditingProject(null);
    } catch (err) {
      console.error('Update error:', err);
      setFailed(true);
      setTimeout(() => {
        setFailed(false);
      }, 3000);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    return status ? 'green' : 'orange';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'featured':
        return 'gold';
      case 'other':
        return 'blue';
      default:
        return 'default';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'featured':
        return <StarFilled className="text-yellow-500" />;
      case 'other':
        return <StarOutlined className="text-blue-500" />;
      default:
        return null;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'web':
        return '🌐';
      case 'mobile':
        return '📱';
      case 'desktop':
        return '🖥️';
      default:
        return '💻';
    }
  };

  return (
    <div>
      {/* Alert Messages */}
      {success && (
        <Alert
          className='mb-6 border border-green-200 bg-green-50 rounded-lg'
          message='Project updated successfully!'
          description='Your changes have been saved to your portfolio.'
          type='success'
          showIcon
          closable
          onClose={() => setSuccess(false)}
        />
      )}

      {failed && (
        <Alert
          className='mb-6 border border-red-200 bg-red-50 rounded-lg'
          message='Update Failed'
          description='There was an error updating your project. Please try again.'
          type='error'
          showIcon
          closable
          onClose={() => setFailed(false)}
        />
      )}

      {/* Projects Grid */}
      {sortedProjects.length > 0 ? (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {sortedProjects.map((project) => (
            <Card
              key={project._id}
              className={`shadow-md border rounded-xl hover:shadow-lg transition-all duration-300 bg-white overflow-hidden ${
                project.priority === 'featured' 
                  ? 'border-yellow-300 bg-gradient-to-br from-yellow-50 to-white' 
                  : 'border-gray-200'
              }`}
              styles={{ body: { padding: '20px' } }}
            >
              {/* Project Media - Now using MediaPreview component */}
              <div className='mb-4 relative'>
                {project.priority === 'featured' && (
                  <div className="absolute top-2 right-2 z-10">
                    <Tag color="gold" icon={<StarFilled />} className="shadow-sm">
                      Featured
                    </Tag>
                  </div>
                )}
                <MediaPreview
                  url={project.url}
                  alt={project.name}
                  className="w-full h-48"
                  placeholderIcon={CodeOutlined}
                  placeholderText="No Preview"
                  placeholderColor="blue"
                  onClick={() => project.url && window.open(project.url, '_blank')}
                  showControls={true}
                  showBadges={true}
                  autoPlayOnHover={true}
                />
              </div>

              {/* Project Header */}
              <div className='mb-3'>
                <div className='flex items-start justify-between mb-2'>
                  <div className='flex items-center space-x-2'>
                    <span className='text-lg'>{getTypeIcon(project.type)}</span>
                    <Tag
                      color={getStatusColor(project.status)}
                      className='text-xs'
                    >
                      {project.status ? 'Completed' : 'In Progress'}
                    </Tag>
                    {project.priority && (
                      <Tag
                        color={getPriorityColor(project.priority)}
                        icon={getPriorityIcon(project.priority)}
                        className='text-xs'
                      >
                        {project.priority === 'featured' ? 'Featured' : 'Other'}
                      </Tag>
                    )}
                  </div>
                  <Text className='text-xs text-gray-500 flex items-center'>
                    <CalendarOutlined className='mr-1' />
                    {formatDate(project.createdAt)}
                  </Text>
                </div>

                <Title level={4} className='mb-2 text-gray-800 line-clamp-1'>
                  {project.name}
                </Title>

                <Text className='text-gray-600 text-sm line-clamp-2 mb-3'>
                  {project.desc}
                </Text>
              </div>

              {/* Tech Stack */}
              {project.stacks && project.stacks.length > 0 && (
                <div className='mb-4'>
                  <div className='flex flex-wrap gap-1'>
                    {project.stacks.slice(0, 4).map((stack, index) => (
                      <Tag
                        key={index}
                        size='small'
                        className='text-xs bg-blue-50 text-blue-700 border-blue-200'
                      >
                        {stack}
                      </Tag>
                    ))}
                    {project.stacks.length > 4 && (
                      <Tag
                        size='small'
                        className='text-xs bg-gray-50 text-gray-600'
                      >
                        +{project.stacks.length - 4} more
                      </Tag>
                    )}
                  </div>
                </div>
              )}

              {/* Project Links */}
              <div className='mb-4 flex items-center space-x-4'>
                {project.link && (
                  <Tooltip title='Live Demo'>
                    <Button
                      type='text'
                      size='small'
                      icon={<GlobalOutlined />}
                      onClick={() => window.open(project.link, '_blank')}
                      className='text-blue-600 hover:text-blue-800 p-1'
                    />
                  </Tooltip>
                )}
                {project.github && (
                  <Tooltip title='GitHub Repository'>
                    <Button
                      type='text'
                      size='small'
                      icon={<GithubOutlined />}
                      onClick={() => window.open(project.github, '_blank')}
                      className='text-gray-600 hover:text-gray-800 p-1'
                    />
                  </Tooltip>
                )}
                <div className='flex-1' />
                <div className='flex items-center space-x-1'>
                  <span
                    className={`w-2 h-2 rounded-full ${
                      project.body &&
                      project.body.trim() &&
                      !isContentEmpty(project.body)
                        ? 'bg-green-500'
                        : 'bg-gray-300'
                    }`}
                  />
                  <Text className='text-xs text-gray-500'>
                    {project.body &&
                    project.body.trim() &&
                    !isContentEmpty(project.body)
                      ? 'Has Content'
                      : 'No Content'}
                  </Text>
                </div>
              </div>

              {/* Action Buttons */}
              <div className='flex items-center justify-between pt-3 border-t border-gray-100'>
                <Space size='small'>
                  <Tooltip title='Edit Content'>
                    <Button
                      type='text'
                      size='small'
                      icon={<EditOutlined />}
                      onClick={() => onEditContent && onEditContent(project)}
                      className='text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                    >
                      Content
                    </Button>
                  </Tooltip>
                </Space>

                <Space size='small'>
                  <Tooltip title='Edit Project'>
                    <Button
                      type='text'
                      size='small'
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(project)}
                      className='text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    >
                      Edit
                    </Button>
                  </Tooltip>
                  <Tooltip title='Delete Project'>
                    <Button
                      type='text'
                      size='small'
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteId(project)}
                      className='text-red-600 hover:text-red-800 hover:bg-red-50'
                      danger
                    >
                      Delete
                    </Button>
                  </Tooltip>
                </Space>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className='text-center py-16'>
          <CodeOutlined className='text-6xl text-gray-400 mb-4' />
          <Title level={3} className='text-gray-500 mb-2'>
            No Development Projects Yet
          </Title>
          <Text className='text-gray-400'>
            Create your first development project to get started
          </Text>
        </div>
      )}

      {/* Edit Modal - Only for basic project info */}
      <Modal
        title={
          <div className='flex items-center space-x-2 pb-4 border-b border-gray-200'>
            <EditOutlined className='text-blue-600' />
            <span className='text-gray-800 font-semibold'>
              Edit Development Project
            </span>
          </div>
        }
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        width={800}
        centered
        className='edit-project-modal-right'
        footer={[
          <Button key='cancel' onClick={handleCancel} className='mr-2'>
            Cancel
          </Button>,
          <Button
            key='submit'
            type='primary'
            onClick={handleOk}
            className='bg-blue-600 hover:bg-blue-700 border-blue-600'
          >
            Update Project
          </Button>,
        ]}
        destroyOnClose
        maskClosable={false}
        getContainer={false}
      >
        <div className='pt-4'>
          <Form
            form={form}
            layout='vertical'
            onFinish={handleFormSubmit}
            onFinishFailed={onFinishFailed}
            size='large'
            className='space-y-4'
          >
            {/* Basic Information */}
            <div className='mb-6'>
              <Title level={5} className='text-gray-800 mb-4 flex items-center'>
                <CheckCircleOutlined className='mr-2 text-blue-600' />
                Basic Information
              </Title>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <Form.Item
                  label={
                    <span className='text-gray-700 font-medium'>
                      Project Name
                    </span>
                  }
                  name='name'
                  rules={[
                    { required: true, message: 'Please enter a project name' },
                  ]}
                >
                  <Input
                    placeholder='Enter project name'
                    className='h-10 border border-gray-300 rounded-lg hover:border-blue-500 focus:border-blue-500'
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span className='text-gray-700 font-medium'>
                      Project Type
                    </span>
                  }
                  name='type'
                >
                  <Select placeholder='Select project type' className='h-10'>
                    <Select.Option value='web'>
                      🌐 Web Application
                    </Select.Option>
                    <Select.Option value='mobile'>
                      📱 Mobile Application
                    </Select.Option>
                    <Select.Option value='desktop'>
                      🖥️ Desktop Application
                    </Select.Option>
                  </Select>
                </Form.Item>
              </div>

              <Form.Item
                label={
                  <span className='text-gray-700 font-medium'>Description</span>
                }
                name='desc'
                rules={[
                  { required: true, message: 'Please enter a description' },
                ]}
              >
                <TextArea
                  rows={3}
                  placeholder='Describe your project...'
                  className='border border-gray-300 rounded-lg hover:border-blue-500 focus:border-blue-500 resize-none'
                />
              </Form.Item>
            </div>

            <Divider className='border-gray-200 my-4' />

            {/* Priority Section */}
            <div className='mb-6'>
              <Title level={5} className='text-gray-800 mb-4 flex items-center'>
                <StarOutlined className='mr-2 text-blue-600' />
                Project Priority
              </Title>

              <Form.Item
                label={
                  <span className='text-gray-700 font-medium'>
                    Priority Level
                  </span>
                }
                name='priority'
                className='max-w-xs'
              >
                <Select
                  placeholder='Select priority (optional)'
                  className='h-10'
                  allowClear
                >
                  <Select.Option value='featured'>⭐ Featured Project</Select.Option>
                  <Select.Option value='other'>📂 Other Project</Select.Option>
                </Select>
              </Form.Item>
              
              <div className='p-3 bg-blue-50 border border-blue-200 rounded-lg mt-2'>
                <Text className='text-blue-700 text-xs'>
                  <strong>💡 Info:</strong> Only one project can be featured at a time.
                </Text>
              </div>
            </div>

            <Divider className='border-gray-200 my-4' />

            {/* Project Links */}
            <div className='mb-6'>
              <Title level={5} className='text-gray-800 mb-4 flex items-center'>
                <LinkOutlined className='mr-2 text-blue-600' />
                Project Links
              </Title>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <Form.Item
                  label={
                    <span className='text-gray-700 font-medium'>
                      Live Demo URL
                    </span>
                  }
                  name='link'
                >
                  <Input
                    placeholder='https://your-project.com'
                    prefix={<LinkOutlined className='text-gray-400' />}
                    className='h-10 border border-gray-300 rounded-lg hover:border-blue-500 focus:border-blue-500'
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span className='text-gray-700 font-medium'>
                      GitHub Repository
                    </span>
                  }
                  name='github'
                >
                  <Input
                    placeholder='https://github.com/username/repo'
                    prefix={<GithubOutlined className='text-gray-400' />}
                    className='h-10 border border-gray-300 rounded-lg hover:border-blue-500 focus:border-blue-500'
                  />
                </Form.Item>
              </div>

              <Form.Item
                label={
                  <span className='text-gray-700 font-medium'>
                    Project Status
                  </span>
                }
                name='status'
                className='max-w-xs'
              >
                <Select placeholder='Select status' className='h-10'>
                  <Select.Option value={false}>🚧 In Development</Select.Option>
                  <Select.Option value={true}>✅ Completed</Select.Option>
                </Select>
              </Form.Item>
            </div>

            <Divider className='border-gray-200 my-4' />

            {/* Technology Stack */}
            <div className='mb-4'>
              <Title level={5} className='text-gray-800 mb-4 flex items-center'>
                <CodeOutlined className='mr-2 text-blue-600' />
                Technology Stack
              </Title>

              <Form.Item name='stacks' className='w-full'>
                <Checkbox.Group className='w-full'>
                  <div className='space-y-4 max-h-60 overflow-y-auto pr-2 w-full'>
                    {Object.entries(groupedStacks).map(([category, stacks]) => (
                      <div
                        key={category}
                        className='bg-gray-50 p-3 rounded-lg border border-gray-200'
                      >
                        <Text className='text-gray-700 font-semibold text-xs uppercase tracking-wide mb-2 block'>
                          {category}
                        </Text>
                        <div className='grid grid-cols-2 sm:grid-cols-3 gap-2'>
                          {stacks.map((stack) => (
                            <Checkbox
                              key={stack.value}
                              value={stack.value}
                              className='text-gray-600 hover:text-gray-800 transition-colors duration-200 text-xs'
                            >
                              <span className='text-xs'>{stack.label}</span>
                            </Checkbox>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </Checkbox.Group>
              </Form.Item>
            </div>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default RenderDevProj;